import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Clock, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePopoverBehavior } from "@/hooks/usePopoverBehavior";
import { formatTime } from "@/lib/timeHelpers";
export { formatTime };  // re-export so DateTimePopup can import from here

// ─── Constants ────────────────────────────────────────────────
// 15-minute intervals across 24 hours → 96 options (exported for reuse)
export const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const totalMinutes = i * 15;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
});

// ─── Main export ──────────────────────────────────────────────
/**
 * TimePopup — time selector that positions a scrollable list popover below the trigger.
 *
 * value:    "HH:MM" (24h) or ""
 * onChange: (hhmm: string) => void
 */
export default function TimePopup({
  value,
  onChange,
  placeholder = "Pick a time",
  id,
  error = false,
  disabled = false,
}) {
  const { open, setOpen, openPopover: openPopoverBase, popoverStyle, triggerRef, popoverRef } =
    usePopoverBehavior({ minWidth: 160 });
  const selectedRef = useRef(null);

  function openPopover() {
    if (disabled) return;
    openPopoverBase();
  }

  // Scroll selected option into view once the portal renders
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      selectedRef.current?.scrollIntoView({ block: "center" });
    }, 0);
    return () => clearTimeout(timer);
  }, [open]);

  function handleSelect(time) {
    onChange(time);
    setOpen(false);
  }

  const displayValue = formatTime(value);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={openPopover}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error ? "border-destructive" : "border-input",
          !displayValue && "text-muted-foreground",
        )}
      >
        <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1">{displayValue || placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-60" />
      </button>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={popoverStyle}
          className="rounded-xl border border-border bg-popover shadow-lg"
        >
          <ul className="max-h-56 overflow-y-auto py-1">
            {TIME_OPTIONS.map((time) => {
              const isSelected = time === value;
              return (
                <li key={time}>
                  <button
                    ref={isSelected ? selectedRef : null}
                    type="button"
                    onClick={() => handleSelect(time)}
                    className={cn(
                      "w-full text-left px-3 py-1.5 text-sm transition-colors",
                      isSelected
                        ? "bg-primary text-primary-foreground"
                        : "hover:bg-muted",
                    )}
                  >
                    {formatTime(time)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
